import { QueryObserverResult } from '@tanstack/react-query';
import { ColumnType } from 'antd/es/table/interface';
import { useMemo } from 'react';
import { useTranslation } from 'translations/hooks';

import { useGraphqlApiHeaders } from 'business/contextProviders/useApiHeaders';
import { ActionsColumn } from 'business/quality-control/pages/segment-history/components/actions-column';
import { RingNumberCell } from 'business/quality-control/pages/segment-history/components/ring-number-cell/ring-number-cell';
import { StatusTag } from 'business/quality-control/pages/segment-history/components/status-tag';
import { SegmentHistoryV1ControllerGetSegmentsHistoryError } from 'generated/apiComponents';
import { SegmentHistoryEntriesV1OutputDto } from 'generated/apiSchemas';
import {
  Module_Enum,
  useGetCurrentRingQuery,
  useGetSegmentHistoryExtraColumnsQuery,
} from 'generated/graphql';
import { formatDate, formatLocalizedDate } from 'technical/format/date';

import { SegmentHistory } from './data/types';

export const useSegmentHistoryColumns = (
  constructionSiteId: string,
  onOpenDrawer: (segment: SegmentHistory) => void,
  onOpenMobileDrawer: (segment: SegmentHistory) => void,
  onOpenEditModal: (segment: SegmentHistory) => void,
  mobile: boolean,
  isDataManager: boolean,
  isConstructionSiteManager: boolean,
  refetchData: () => Promise<
    QueryObserverResult<
      SegmentHistoryEntriesV1OutputDto,
      SegmentHistoryV1ControllerGetSegmentsHistoryError
    >
  >,
) => {
  const { t } = useTranslation();

  const graphqlHeaders = useGraphqlApiHeaders(Module_Enum.QualityScan);
  const { data: extraColumnsData } = useGetSegmentHistoryExtraColumnsQuery({
    variables: {
      constructionSiteId,
    },
    context: { headers: graphqlHeaders },
  });

  const { data: currentRingData } = useGetCurrentRingQuery({
    variables: { constructionSiteId },
    context: { headers: graphqlHeaders },
  });

  const currentRingNumber =
    currentRingData?.parameter_ring_aggregate?.aggregate?.max?.ringNumber ??
    null;

  const extraColumns = useMemo(() => {
    const commonColumnProps: Partial<ColumnType<SegmentHistory>> = {
      align: 'center',
    };

    return (extraColumnsData?.extraColumns ?? []).map((column) => {
      return {
        title: column.segmentParameter,
        dataIndex: column.segmentParameter,
        key: column.id,
        width: 100,
        sorter: true,
        render: (value: string) => <span>{value ? value : '-'}</span>,
        ...commonColumnProps,
      };
    });
  }, [extraColumnsData]);

  return useMemo(() => {
    const commonColumnProps: Partial<ColumnType<SegmentHistory>> = {
      align: 'center',
    };

    if (mobile) {
      const mobileColumnProps = {
        ...commonColumnProps,
        onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' as const } }),
      };

      return [
        {
          title: t('qualityControl.segmentHistory.columns.serialNumber'),
          dataIndex: 'serialNumber',
          key: 'serialNumber',
          width: 100,
          sorter: true,
          ...mobileColumnProps,
        },
        {
          title: t('qualityControl.segmentHistory.columns.status'),
          dataIndex: 'status',
          key: 'status',
          width: 75,
          sorter: true,
          render: (status: string) => <StatusTag status={status} />,
          ...mobileColumnProps,
        },
        {
          title: t('qualityControl.segmentHistory.columns.ring'),
          dataIndex: 'ringNumber',
          key: 'ringNumber',
          width: 75,
          sorter: true,
          render: (ringNumber: number) => (
            <RingNumberCell
              ringNumber={ringNumber}
              currentRingNumber={currentRingNumber}
            />
          ),
          ...mobileColumnProps,
        },
        {
          key: 'actions',
          render: (_, record) => {
            return (
              <ActionsColumn
                record={record}
                onOpenDrawer={onOpenMobileDrawer}
                mobile
              />
            );
          },
          width: 50,
          ...mobileColumnProps,
        },
      ] satisfies ColumnType<SegmentHistory>[];
    }

    return [
      {
        title: t('qualityControl.segmentHistory.columns.serialNumber'),
        dataIndex: 'serialNumber',
        key: 'serialNumber',
        fixed: true,
        width: 130,
        sorter: true,
        ...commonColumnProps,
      },
      {
        title: t('qualityControl.segmentHistory.columns.segmentType'),
        dataIndex: 'segmentType',
        key: 'segmentType',
        width: 95,
        sorter: true,
        render: (segmentType: string) => (
          <span>{segmentType ? segmentType : '-'}</span>
        ),
        ...commonColumnProps,
      },
      {
        title: t('qualityControl.segmentHistory.columns.mold'),
        dataIndex: 'mold',
        key: 'mold',
        width: 110,
        sorter: true,
        render: (mold: string) => <span>{mold ? mold : '-'}</span>,
        ...commonColumnProps,
      },

      // Extra columns emplacement
      ...extraColumns,

      {
        title: t('qualityControl.segmentHistory.columns.manufactureDate'),
        dataIndex: 'manufactureDate',
        key: 'manufactureDate',
        width: 130,
        sorter: true,
        render: (date: string) => {
          return <span>{formatLocalizedDate(date)}</span>;
        },
        ...commonColumnProps,
      },
      {
        title: t('qualityControl.segmentHistory.columns.scanSite'),
        dataIndex: 'lastScanSite',
        key: 'lastScanSite',
        width: 100,
        sorter: true,
        render: (site: string) => <span>{site ? site : '-'}</span>,
        ...commonColumnProps,
      },
      {
        title: t('qualityControl.segmentHistory.columns.surfaceScan'),
        dataIndex: 'hasSurfaceScan',
        key: 'hasSurfaceScan',
        width: 100,
        sorter: true,
        render: (hasSurfaceScan: boolean) => (
          <span>{hasSurfaceScan ? '✅' : '🟠'}</span>
        ),
        ...commonColumnProps,
      },
      {
        title: t('qualityControl.segmentHistory.columns.tunnelScan'),
        dataIndex: 'hasTunnelScan',
        key: 'hasTunnelScan',
        width: 85,
        sorter: true,
        render: (hasTunnelScan: boolean) => (
          <span>{hasTunnelScan ? '✅' : '🟠'}</span>
        ),
        ...commonColumnProps,
      },
      {
        title: t('qualityControl.segmentHistory.columns.scanDate'),
        dataIndex: 'lastScanDate',
        key: 'lastScanDate',
        width: 130,
        sorter: true,
        render: (date: string) => <span>{formatDate(date)}</span>,
        ...commonColumnProps,
      },
      {
        title: t('qualityControl.segmentHistory.columns.status'),
        dataIndex: 'status',
        key: 'status',
        width: 110,
        sorter: true,
        render: (status: string) => <StatusTag status={status} />,
        ...commonColumnProps,
      },
      {
        title: t('qualityControl.segmentHistory.columns.ring'),
        dataIndex: 'ringNumber',
        key: 'ringNumber',
        width: 85,
        sorter: true,
        render: (ringNumber: number) => (
          <RingNumberCell
            ringNumber={ringNumber}
            currentRingNumber={currentRingNumber}
          />
        ),
        ...commonColumnProps,
      },
      {
        title: t('qualityControl.segmentHistory.columns.actions'),
        key: 'actions',
        render: (_, record) => {
          return (
            <ActionsColumn
              record={record}
              onOpenDrawer={onOpenDrawer}
              onOpenEditModal={onOpenEditModal}
              isDataManager={isDataManager}
              isConstructionSiteManager={isConstructionSiteManager}
              refetchData={refetchData}
            />
          );
        },
        width: 85,
        ...commonColumnProps,
      },
    ] satisfies ColumnType<SegmentHistory>[];
  }, [
    mobile,
    t,
    extraColumns,
    currentRingNumber,
    onOpenMobileDrawer,
    onOpenDrawer,
    onOpenEditModal,
    isDataManager,
    isConstructionSiteManager,
    refetchData,
  ]);
};
